"use client";

/**
 * Per-model, per-field renderer overrides for `ModelDetailView`.
 *
 * Backed by a Jotai atom on the shared graph-store, so apps register
 * their overrides once at module-import time and every
 * `ModelDetailView` instance — anywhere in the route tree — picks them
 * up via `useFieldOverrides(model)`. No Provider wrapper, no
 * route-group coupling.
 *
 * Use case: `AttributeValue.value` is typed `Any` because its runtime
 * type is constrained by the linked `Attribute` node. The generic
 * `EditableField` (Zod-driven) can't see that constraint; a custom
 * editor that reads `av.attribute` can.
 *
 * App side (called once at import):
 *
 *     import { setFieldOverrides } from "@sourcepilot/ui/framework/ui/views";
 *     setFieldOverrides({
 *       AttributeValue: { value: AttributeValueValueEditor },
 *     });
 */

import React from "react";
import { atom, useAtomValue } from "jotai";
import { graphStoreJotaiStore } from "@sourcepilot/framework/graph-store";

/**
 * Per-field renderer component. Receives the `editable` wrapper AND the
 * loaded `node` (in case it needs to read sibling relationships).
 */
export type FieldOverride = React.ComponentType<{
  editable: any;
  node: any;
}>;

/** Model name → field name → component. */
export type FieldOverridesRegistry = Record<
  string,
  Record<string, FieldOverride>
>;

const fieldOverridesAtom = atom<FieldOverridesRegistry>({});

/**
 * Register the app's field-override map. Call once at app boot; calling
 * again replaces the entire registry. Lives on the graph-store's Jotai
 * store so it's a global one-shot, not a per-Provider scope.
 */
export function setFieldOverrides(registry: FieldOverridesRegistry): void {
  graphStoreJotaiStore.set(fieldOverridesAtom, registry);
}

/**
 * Look up the override map for a given model. Returns an empty object
 * when no overrides are registered (the common case).
 */
export function useFieldOverrides(
  model: string,
): Record<string, FieldOverride> {
  const registry = useAtomValue(fieldOverridesAtom, {
    store: graphStoreJotaiStore,
  });
  return registry[model] ?? {};
}
